import { create } from 'zustand';
import { detectQuality } from './config';
import type {
  GamePhase,
  LockerBeat,
  PlayerProfile,
  PreferredFoot,
  QualityPreset,
  ShotSide,
} from './types';
import type { SkillCoreDef, SkillId } from '../data/skills';
import { skillBoards } from '../data/skillBoards';
import { TUNNEL_START_Z } from '../data/tunnel';

const defaultProfile: PlayerProfile = {
  name: 'PLAYER',
  number: 7,
  flag: 'IN',
  foot: 'right',
};

interface GameStore {
  phase: GamePhase;
  profile: PlayerProfile;
  quality: QualityPreset;
  unlockedSkills: SkillId[];
  activeCore: SkillCoreDef | null;
  overlayOpacity: number;
  timeScale: number;
  muted: boolean;
  shotSide: ShotSide;
  shotAim: { x: number; y: number };
  shotPower: number;
  keeperDiveSide: ShotSide;
  keeperDiveX: number;
  runnerZ: number;
  /** Tunnel FP yaw (radians) — radar / nav HUD */
  runnerYaw: number;
  /** Tunnel FP lateral X */
  runnerX: number;
  activeArena: string | null;
  showRealIdentity: boolean;
  tunnelReady: boolean;
  tunnelWalking: boolean;
  /** Mount tunnel GL under black/iris before phase flips */
  tunnelWarming: boolean;
  activeTunnelPanel: string | null;
  /** 0–1 match stamina — fills as you walk the tunnel */
  tunnelStamina: number;
  /** Latest unlocked board label for HUD toast */
  careerToast: string | null;
  careerToastAt: number;
  /** Until performance.now() — brief unlock slowdown */
  unlockSlowUntil: number;
  lockerBeat: LockerBeat;
  lockerDoorClosed: boolean;
  /** True at grasp moment — HungJersey plays pull pulse */
  lockerClaimTouched: boolean;
  /** 0 = intact cloth, 1 = fully dissolved (claim beat). */
  lockerDissolve: number;
  /**
   * Claim lighting helper (kept minimal — dissolve is quiet, no flash/magic).
   */
  lockerCine: {
    on: boolean;
    wash: number;
    spot: number;
    flicker: number;
    flash: number;
    particles: boolean;
  };
  setPhase: (phase: GamePhase) => void;
  setProfile: (partial: Partial<PlayerProfile>) => void;
  setName: (name: string) => void;
  setNumber: (number: number) => void;
  setFlag: (flag: string) => void;
  setFoot: (foot: PreferredFoot) => void;
  setOverlayOpacity: (v: number) => void;
  setTimeScale: (v: number) => void;
  setMuted: (v: boolean) => void;
  setTunnelReady: (v: boolean) => void;
  setLockerBeat: (beat: LockerBeat) => void;
  setLockerDoorClosed: (v: boolean) => void;
  setLockerCine: (partial: Partial<GameStore['lockerCine']>) => void;
  setLockerDissolve: (v: number) => void;
  startExperience: () => void;
  showLockerIdentity: () => void;
  confirmLockerIdentity: () => void;
  finishLockerClaim: () => void;
  setLockerClaimTouched: (v: boolean) => void;
  startTunnelWarming: () => void;
  enterTunnelFromLocker: () => void;
  startTunnelWalk: () => void;
  setActiveTunnelPanel: (id: string | null) => void;
  setRunnerZ: (z: number) => void;
  setRunnerPose: (pose: { z?: number; x?: number; yaw?: number }) => void;
  setTunnelStamina: (v: number) => void;
  setActiveArena: (id: string | null) => void;
  unlockSkill: (id: SkillId) => void;
  triggerUnlockSlow: (ms?: number) => void;
  clearCareerToast: () => void;
  showCoreReveal: (core: SkillCoreDef) => void;
  clearCoreReveal: () => void;
  finishLoading: () => void;
  startRun: () => void;
  startShot: () => void;
  resolveShot: (
    side: ShotSide,
    aim?: { x: number; y: number },
    power?: number,
  ) => void;
  startKeeper: () => void;
  resolveKeeper: () => void;
  startCinematic: () => void;
  revealIdentity: () => void;
  finishCinematic: () => void;
  finishStadium: () => void;
  finishRun: () => void;
  skipToReveal: () => void;
  /** Ending handoff — skip cinematic/stadium, assemble portfolio EA-FC style */
  finishEnding: () => void;
  endingAssemble: boolean;
  clearEndingAssemble: () => void;
  replay: () => void;
}

export const useGameStore = create<GameStore>((set, get) => ({
  phase: 'loading',
  profile: { ...defaultProfile },
  quality: detectQuality(),
  unlockedSkills: [],
  activeCore: null,
  overlayOpacity: 0,
  timeScale: 1,
  muted: false,
  endingAssemble: false,
  shotSide: 1,
  shotAim: { x: 0.5, y: 0.42 },
  shotPower: 0.6,
  keeperDiveSide: -1,
  keeperDiveX: -2.4,
  runnerZ: TUNNEL_START_Z,
  runnerYaw: Math.PI,
  runnerX: 0,
  activeArena: null,
  showRealIdentity: false,
  tunnelReady: false,
  tunnelWalking: false,
  tunnelWarming: false,
  activeTunnelPanel: null,
  tunnelStamina: 0.1,
  careerToast: null,
  careerToastAt: 0,
  unlockSlowUntil: 0,
  lockerBeat: 'cinematic',
  lockerDoorClosed: false,
  lockerClaimTouched: false,
  lockerDissolve: 0,
  lockerCine: {
    on: false,
    wash: 1,
    spot: 1,
    flicker: 1,
    flash: 0,
    particles: false,
  },
  setPhase: (phase) => set({ phase }),
  setProfile: (partial) =>
    set((s) => ({ profile: { ...s.profile, ...partial } })),
  setName: (name) =>
    set((s) => ({
      profile: {
        ...s.profile,
        name: name
          .toUpperCase()
          .replace(/[^A-Z0-9 ]/g, '')
          .slice(0, 12),
      },
    })),
  setNumber: (number) =>
    set((s) => ({
      profile: {
        ...s.profile,
        number: Math.max(1, Math.min(99, Math.floor(number) || 1)),
      },
    })),
  setFlag: (flag) => set((s) => ({ profile: { ...s.profile, flag } })),
  setFoot: (foot) => set((s) => ({ profile: { ...s.profile, foot } })),
  setOverlayOpacity: (overlayOpacity) => set({ overlayOpacity }),
  setTimeScale: (timeScale) => set({ timeScale }),
  setMuted: (muted) => set({ muted }),
  setTunnelReady: (tunnelReady) => set({ tunnelReady }),
  setLockerBeat: (lockerBeat) => set({ lockerBeat }),
  setLockerDoorClosed: (lockerDoorClosed) => set({ lockerDoorClosed }),
  setLockerCine: (partial) =>
    set((s) => ({ lockerCine: { ...s.lockerCine, ...partial } })),
  setLockerDissolve: (lockerDissolve) =>
    set({ lockerDissolve: Math.max(0, Math.min(1, lockerDissolve)) }),
  startExperience: () =>
    set({
      phase: 'locker',
      lockerBeat: 'cinematic',
      lockerDoorClosed: false,
      lockerClaimTouched: false,
      lockerDissolve: 0,
      lockerCine: {
        on: false,
        wash: 1,
        spot: 1,
        flicker: 1,
        flash: 0,
        particles: false,
      },
      overlayOpacity: 0,
      tunnelReady: false,
      tunnelWalking: false,
      profile: { ...defaultProfile },
    }),
  showLockerIdentity: () => set({ lockerBeat: 'identity' }),
  confirmLockerIdentity: () =>
    set({
      lockerBeat: 'claim',
      lockerClaimTouched: false,
      lockerDissolve: 0,
    }),
  finishLockerClaim: () =>
    set({
      lockerBeat: 'closing',
      lockerDoorClosed: true,
      lockerClaimTouched: true,
      lockerDissolve: 1,
      lockerCine: {
        on: false,
        wash: 1,
        spot: 1,
        flicker: 1,
        flash: 0,
        particles: false,
      },
    }),
  setLockerClaimTouched: (lockerClaimTouched) => set({ lockerClaimTouched }),
  startTunnelWarming: () =>
    set({
      tunnelWarming: true,
      overlayOpacity: 1,
    }),
  enterTunnelFromLocker: () =>
    set({
      phase: 'tunnel',
      tunnelReady: true,
      tunnelWalking: true,
      tunnelWarming: false,
      lockerBeat: 'walk',
      // Stay covered until iris opens (caller reveals)
      overlayOpacity: 1,
      unlockedSkills: [],
      activeTunnelPanel: null,
      runnerZ: TUNNEL_START_Z,
      runnerYaw: Math.PI,
      runnerX: 0,
      tunnelStamina: 0.1,
      careerToast: null,
      unlockSlowUntil: 0,
    }),
  startTunnelWalk: () =>
    set({
      tunnelWalking: true,
      unlockedSkills: [],
      activeTunnelPanel: null,
      overlayOpacity: 0,
      tunnelStamina: 0.1,
      careerToast: null,
      unlockSlowUntil: 0,
      runnerZ: TUNNEL_START_Z,
      runnerYaw: Math.PI,
      runnerX: 0,
    }),
  setActiveTunnelPanel: (activeTunnelPanel) => set({ activeTunnelPanel }),
  setRunnerZ: (runnerZ) => set({ runnerZ }),
  setRunnerPose: (pose) =>
    set((s) => ({
      runnerZ: pose.z ?? s.runnerZ,
      runnerX: pose.x ?? s.runnerX,
      runnerYaw: pose.yaw ?? s.runnerYaw,
    })),
  setTunnelStamina: (tunnelStamina) =>
    set({ tunnelStamina: Math.max(0, Math.min(1, tunnelStamina)) }),
  setActiveArena: (activeArena) => set({ activeArena }),
  unlockSkill: (id) => {
    const s = get();
    if (s.unlockedSkills.includes(id)) return;
    const board = skillBoards.find((b) => b.id === id);
    const label = board
      ? `${board.title} ${board.subtitle}`.replace(/\s+/g, ' ').trim()
      : id;
    set({
      unlockedSkills: [...s.unlockedSkills, id],
      careerToast: label,
      careerToastAt: performance.now(),
    });
  },
  triggerUnlockSlow: (ms = 500) =>
    set({ unlockSlowUntil: performance.now() + ms }),
  clearCareerToast: () => set({ careerToast: null }),
  showCoreReveal: (activeCore) => set({ activeCore }),
  clearCoreReveal: () => set({ activeCore: null }),
  finishLoading: () =>
    set({
      phase: 'landing',
      overlayOpacity: 0,
      tunnelReady: false,
      tunnelWalking: false,
      tunnelWarming: false,
      lockerBeat: 'cinematic',
      lockerDoorClosed: false,
      lockerDissolve: 0,
      lockerCine: {
        on: false,
        wash: 1,
        spot: 1,
        flicker: 1,
        flash: 0,
        particles: false,
      },
    }),
  startRun: () =>
    set({
      phase: 'run',
      overlayOpacity: 0,
      unlockedSkills: [],
      showRealIdentity: false,
      activeCore: null,
      tunnelWalking: false,
      tunnelWarming: false,
    }),
  startShot: () =>
    set({
      phase: 'shot',
      // Keep black during ShotAim mount — ShotAim fades in (kills blue flash)
      overlayOpacity: 1,
      activeCore: null,
      timeScale: 1,
      tunnelWalking: false,
      tunnelWarming: false,
      tunnelStamina: 1,
      careerToast: null,
      unlockSlowUntil: 0,
    }),
  resolveShot: (side, aim, power) => {
    const aimX = aim?.x ?? (side < 0 ? 0.28 : 0.72);
    const shotSide: ShotSide = aimX < 0.5 ? -1 : 1;
    const keeperDiveSide: ShotSide = shotSide === 1 ? -1 : 1;
    const keeperDiveX = keeperDiveSide * 2.55;
    set({
      shotSide,
      keeperDiveSide,
      keeperDiveX,
      shotAim: aim ?? { x: aimX, y: 0.35 },
      shotPower: power ?? 0.65,
      phase: 'keeper',
      overlayOpacity: 0,
    });
  },
  startKeeper: () => set({ phase: 'keeper', overlayOpacity: 0 }),
  resolveKeeper: () => set({ phase: 'cinematic', overlayOpacity: 0 }),
  startCinematic: () => set({ phase: 'cinematic', overlayOpacity: 0 }),
  revealIdentity: () => set({ showRealIdentity: true }),
  finishCinematic: () =>
    set({ phase: 'stadium', overlayOpacity: 0, activeCore: null }),
  finishStadium: () =>
    set({ phase: 'reveal', overlayOpacity: 0, activeCore: null }),
  finishRun: () => set({ phase: 'reveal', overlayOpacity: 0, activeCore: null }),
  skipToReveal: () =>
    set({
      phase: 'reveal',
      overlayOpacity: 0,
      activeCore: null,
      showRealIdentity: true,
    }),
  finishEnding: () =>
    set({
      phase: 'reveal',
      overlayOpacity: 0,
      activeCore: null,
      showRealIdentity: true,
      endingAssemble: true,
      timeScale: 1,
    }),
  clearEndingAssemble: () => set({ endingAssemble: false }),
  replay: () =>
    set({
      phase: 'landing',
      profile: { ...defaultProfile },
      unlockedSkills: [],
      activeCore: null,
      endingAssemble: false,
      overlayOpacity: 0,
      timeScale: 1,
      shotSide: 1,
      keeperDiveSide: -1,
      keeperDiveX: -2.4,
      runnerZ: TUNNEL_START_Z,
      runnerYaw: Math.PI,
      runnerX: 0,
      activeArena: null,
      showRealIdentity: false,
      tunnelReady: false,
      tunnelWalking: false,
      tunnelWarming: false,
      activeTunnelPanel: null,
      lockerBeat: 'cinematic',
      lockerDoorClosed: false,
      lockerDissolve: 0,
      lockerCine: {
        on: false,
        wash: 1,
        spot: 1,
        flicker: 1,
        flash: 0,
        particles: false,
      },
    }),
}));

// Dev-only handle for headless scene checks (tools/verify-exit.mjs).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__game = useGameStore;
}
